import { ViewMode } from '@efdigital/ll-addon-codemirror';
import MarkDown from '@efdigital/ll-addon-content/components/mark-down';
import type { CalendarItem, Message } from '@efdigital/ll-addon-data';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { MarkdownEditorWithImagePicker } from '@efdigital/ll-admin-v2/components/markdown-editor-with-image-picker';
import { setAsyncHasMany } from '@efdigital/ll-admin-v2/data/async-setter';
import { NoEmoji } from '@efdigital/ll-admin-v2/lib/markdown-toolbar';
import { fn } from '@ember/helper';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

import { InputDateTime } from '../../input-date.gts';
import CalendarItemsPicker from '../../pickers/calendar-items-picker.gts';
import CalendarTable from '../calendar/calendar-table.gts';

function mapIds(calendarItems: CalendarItem[]): string[] {
  return calendarItems.map((item) => item.id!);
}

export class EditMessage extends Component<
  EditRecordSignature<
    Message,
    {
      programId: string;
      cohortId: string;
      showContent: boolean;
      showCalendarItems: boolean;
    }
  >
> {
  get markdown(): string {
    return this.args.item.markdown || '';
  }

  setMarkdown = (value: string) => {
    this.args.item.markdown = value ?? '';
  };

  updateTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  setStartDate = (value: string) => {
    this.args.item.startDate = value;
  };

  setCalendarItems = (calendarItems: CalendarItem[]) => {
    setAsyncHasMany(this.args.item, 'calendarItems', calendarItems);
  };

  get category(): string {
    return this.args.item.category || 'announcement';
  }

  get editButtonLabel(): string {
    if (this.args.showCalendarItems && this.args.showContent) {
      return 'Edit Announcement';
    } else if (this.args.showCalendarItems) {
      return 'Edit Events';
    } else if (this.args.showContent) {
      return 'Edit Content';
    }
    return 'Edit Announcement';
  }

  <template>
    <EditRecord
      @editLabel={{this.editButtonLabel}}
      @item={{@item}}
      @context={{@context}}
      @initialState={{if @item.id "readonly" "edit"}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        {{#if @showContent}}
          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Content</legend>

            {{! Title }}
            <div class="mb-3">
              <label for="{{uniqueId}}-title" class="form-label required">
                Title
              </label>
              <input
                type="text"
                id="{{uniqueId}}-title"
                class="form-control"
                value={{@item.title}}
                placeholder="Title"
                required
                readonly={{isReadOnly}}
                {{on "input" this.updateTitle}}
              />
            </div>

            {{! Markdown Content }}
            <div class="mb-3">
              <label for="{{uniqueId}}-body" class="form-label required">
                Message
              </label>
              {{#if @item}}
                {{#if isReadOnly}}
                  <textarea
                    id="{{uniqueId}}-body"
                    class="form-control"
                    rows="10"
                    required
                    readonly
                  >{{this.markdown}}</textarea>
                {{else}}
                  <MarkdownEditorWithImagePicker
                    @markdown={{this.markdown}}
                    @onUpdate={{this.setMarkdown}}
                    @mode={{ViewMode.SplitHorizontal}}
                    @toolbar={{NoEmoji}}
                    @requiredSize="email-image"
                  >
                    <:preview as |markdown|>
                      <MarkDown @markdown={{markdown}} />
                    </:preview>
                  </MarkdownEditorWithImagePicker>
                {{/if}}
              {{/if}}
            </div>

            {{! Publish Date }}
            <div class="mb-3">
              <label for="{{uniqueId}}-publishDate" class="form-label">
                Publish Date
              </label>
              <InputDateTime
                id="{{uniqueId}}-publishDate"
                name="publishDate"
                class="form-control"
                readonly={{isReadOnly}}
                @value={{@item.startDate}}
                @onChange={{this.setStartDate}}
              />
              <div class="form-text">Leave blank to publish immediately.</div>
            </div>
          </fieldset>
        {{/if}}

        {{#if @showCalendarItems}}
          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Associated Events</legend>

            <Await @promise={{@item.calendarItems}}>
              <:success as |calendarItems|>
                <div class="mb-3">
                  <label
                    for="{{uniqueId}}-calendarItems"
                    class="form-label visually-hidden"
                  >
                    Events associated with this
                    {{this.category}}
                  </label>

                  {{#if calendarItems.length}}
                    <CalendarTable @calendarItems={{calendarItems}}>
                      <:title as |calendarItem|>
                        {{#if isReadOnly}}
                          <LinkTo
                            @route="programs.program.cohort.schedule.calendar-item"
                            @models={{array
                              @programId
                              @cohortId
                              calendarItem.id
                            }}
                          >{{calendarItem.title}}</LinkTo>
                        {{else}}
                          {{calendarItem.title}}
                        {{/if}}
                      </:title>
                    </CalendarTable>
                  {{else}}
                    <div class="alert alert-info">
                      No events associated with this
                      {{this.category}}.
                    </div>
                  {{/if}}

                  {{#unless isReadOnly}}
                    <CalendarItemsPicker
                      @programId={{@programId}}
                      @cohortId={{@cohortId}}
                      @onSelect={{this.setCalendarItems}}
                    >
                      <:input as |showModal|>
                        <div class="mb-3">
                          <button
                            type="button"
                            class="btn btn-primary btn-sm"
                            {{on "click" (fn showModal (mapIds calendarItems))}}
                          >Select Events</button>
                        </div>
                      </:input>
                    </CalendarItemsPicker>
                  {{/unless}}
                </div>
              </:success>
            </Await>
          </fieldset>
        {{/if}}
      </:form>
    </EditRecord>
  </template>
}
